import { useURL } from '@kalyzee/kast-web-sdk/web-components';
import { useEffect } from 'react';
import { SessionMode } from '../../contexts/appStore/hooks';
import { AppLoadingView } from '../../components/utils/AppLoadingView';
import { LoadingView } from '../../components/utils/LoadingView';
import { PAGE_PATHS } from '../../components/navigation/PageContext';
import { useAppNavigate } from './AppNavigate';
import { getGlobalQueryLabel, NavigateGlobalQuery } from './queries';

interface PublicRedirectionRouteProps {
  mode: SessionMode;
}

const PublicRedirect = ({ mode }: PublicRedirectionRouteProps) => {
  const url = useURL();
  const navigate = useAppNavigate();

  useEffect(() => {
    const currentPath = url.pathname.startsWith('/') ? url.pathname.replace('/', '') : url.pathname;

    const newUrl = new URL(url);
    newUrl.pathname = PAGE_PATHS[SessionMode.MASTER].login;
    newUrl.search = '';

    const isExplicitLogout = !!sessionStorage.getItem('explicit_logout');
    if (isExplicitLogout) {
      // explicit_logout => User just hits logout button
      // He is still on a logged page, we send him login without a ?redirect=
      sessionStorage.removeItem('explicit_logout');
    } else {
      // no explicit_logout
      // user tried to access this page without auth
      // we send him to login with a ?redirect=
      if (currentPath.length && currentPath !== 'logout' && currentPath !== 'login') {
        // searchParams.set percent-encodes the value once — enough for the
        // embedded `?`/`&` to survive; consumers read it back with a single decode.
        const redirect = `${currentPath}${url.search}`;
        newUrl.searchParams.set(getGlobalQueryLabel(NavigateGlobalQuery.REDIRECT), redirect);
      }
    }

    // Sticky queries end up both at the login top level (via useAppNavigate)
    // and inside `redirect`, so they apply on /login and after login.
    navigate(newUrl.pathname + newUrl.search, { replace: true });
  }, [url, navigate]);

  // Until navigate() commits the redirect (next render), this route element
  // is what's on screen. Render the same chrome (topbar + AnimatedBackground
  // via AppLoadingView) so logout → login doesn't flash an empty page.
  // Slave mode skips MasterUI since it doesn't use the master chrome.
  if (mode === SessionMode.MASTER) return <AppLoadingView />;

  return <LoadingView fullscreen />;
};

export default PublicRedirect;
